import { Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import {
  BankReconciliationService,
  BankEnum,
  ColumnNameEnum,
  Currency,
} from '../../../service/bank-reconciliation.service';
import { bankOptions } from '../banks.list';

interface MappingRequest {
  columnName: ColumnNameEnum | null;
  columnIndex: number | null;
}

@Component({
  selector: 'app-bank-management',
  standalone: true,
  imports: [FormsModule, TabViewModule, ToastModule, ButtonModule, DropdownModule, InputNumberModule, FileUploadModule],
  templateUrl: './bank-management.component.html',
  styleUrl: './bank-management.component.scss',
  providers: [MessageService]
})
export class BankManagementComponent implements OnInit {

  @ViewChild('fileUpload') fileUpload: any;
  private readonly service = inject(BankReconciliationService);
  messageService = inject(MessageService);

  banks = bankOptions;
  currencies = signal<Currency[]>([]);
  loadingCurrencies = signal(false);

  columnNameOptions: { label: string; value: ColumnNameEnum }[] = [
    { label: 'Transaction Date', value: 'TRANSACTION_DATE' },
    { label: 'Value Date', value: 'VALUE_DATE' },
    { label: 'Description', value: 'DESCRIPTION' },
    { label: 'Reference', value: 'REFERENCE' }, 
    { label: 'Debit', value: 'DEBIT' },
    { label: 'Credit', value: 'CREDIT' },
    { label: 'Balance', value: 'BALANCE' },
  ];
  

  mappingBank: BankEnum | null = null;
  headersToSkip: number = 0;
  mappingRequests = signal<MappingRequest[]>([{ columnName: null, columnIndex: null }]);

  importBank: BankEnum | null = null;
  importCurrencyId: number | null = null;
  selectedFile: File | null = null;

  savingMapping = signal(false);
  importing = signal(false);

  ngOnInit(): void {
    this.loadCurrencies();
  }

  private loadCurrencies(): void {
    this.loadingCurrencies.set(true);
    this.service.getCurrencies().subscribe({
      next: (res) => {
        this.currencies.set(res.currencies ?? []);
        this.loadingCurrencies.set(false);
      },
      error: () => this.loadingCurrencies.set(false),
    });
  }

  addMappingRow(): void {
    this.mappingRequests.update(rows => [...rows, { columnName: null, columnIndex: null }]);
  }

  removeMappingRow(index: number): void {
    this.mappingRequests.update(rows => rows.filter((_, i) => i !== index));
  }

  submitBankMapping(): void {
    if (!this.mappingBank) return;
    const validRows = this.mappingRequests().filter(r => r.columnName && r.columnIndex != null);
    if (!validRows.length) {
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Add at least one complete mapping row' });
      return;
    }
    this.savingMapping.set(true);
    this.service.saveBankMapping({
      bank: this.mappingBank,
      numberOfHeadersToSkip: this.headersToSkip ?? 0,
      bankMappingRequests: validRows.map(r => ({
        columnName: r.columnName!,
        columnIndex: r.columnIndex!,
      })),
    }).subscribe({
      next: () => {
        this.savingMapping.set(false);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Bank mapping saved' });
        this.mappingBank = null;
        this.headersToSkip = 0;
        this.mappingRequests.set([{ columnName: null, columnIndex: null }]);
      },
      error: (err: any) => {
        this.savingMapping.set(false);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message ?? 'Failed to save mapping' });
      },
    });
  }

  onFileSelect(event: any): void {
    this.selectedFile = event.files?.[0] ?? null;
  }

  submitStatementImport(): void {
    if (!this.importBank || !this.importCurrencyId || !this.selectedFile) return;
    this.importing.set(true);
    this.service.importStatement(this.selectedFile, this.importBank, this.importCurrencyId).subscribe({
      next: (res) => {
        this.importing.set(false);
        this.messageService.add({
          severity: 'success', summary: 'Success',
          detail: res.message ?? `Imported ${res.bankReconciliationTransactions?.length ?? 0} transactions`
        });
        this.importBank = null;
        this.importCurrencyId = null;
        this.selectedFile = null;
        this.fileUpload?.clear();
      },
      error: (err: any) => {
        this.importing.set(false);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message ?? 'Failed to import statement' });
      },
    });
  }
}
